import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, Package, Plus, Printer, ScanLine } from "lucide-react";
import { WineFormDialog } from "@/components/WineFormDialog";
import { BottleChips } from "@/components/BottleChips";
import { SelectionBar, toggleIn } from "@/components/SelectionBar";
import { BottleDetailsDialog } from "@/components/BottleDetailsDialog";
import { api, openLabels } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DrinkBadge } from "@/components/DrinkBadge";
import { InventoryPanel } from "@/components/InventoryPanel";
import { InventoryHistory } from "@/components/InventoryHistory";

export default function LocationContents() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [checks, setChecks] = useState([]);
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [picked, setPicked] = useState([]);

  const loadChecks = useCallback(() => api.get(`/locations/${id}/inventory`).then((r) => setChecks(r.data)).catch(() => {}), [id]);
  const load = useCallback(() => api.get(`/locations/${id}/contents`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Location not found")), [id]);
  useEffect(() => {
    load();
    loadChecks();
  }, [load, loadChecks]);

  if (error) return <p className="px-8 py-12 text-[#E5A93C]" data-testid="location-contents-error">{error}</p>;
  if (!data) return <p className="px-8 py-12 font-mono text-sm text-[#6E665D]">Loading…</p>;

  const print = (only) => {
    const labels = [
      ...data.boxes.map((b) => ({ barcode: b.barcode, kind: "box", box_number: b.box_number, title: `Box ${b.box_number}`, sub: data.location.name, wine: `Box ${b.box_number} · ${data.location.name}`, extra: `${b.bottle_count} btl` })),
      ...data.wines.flatMap((w) => w.bottles.map((b) => ({ barcode: b.barcode, title: w.name, sub: `${w.producer || ""} ${w.vintage || "NV"}`.trim(), wine: `${w.name} ${w.vintage || "NV"}`, extra: b.box_number ? `Box ${b.box_number}` : "" }))),
    ].filter((l) => !only || only.includes(l.barcode));
    openLabels(labels, navigate);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
      <button onClick={() => navigate("/locations")} className="flex items-center gap-2 text-sm text-[#A89F91] hover:text-[#D4AF37] mb-6" data-testid="back-to-locations"><ArrowLeft className="w-4 h-4" /> Locations</button>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 rise">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Location</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB]" data-testid="location-contents-name">{data.location.name}</h2>
          <p className="text-sm text-[#A89F91] mt-2" data-testid="location-contents-summary">{data.bottle_count} bottle{data.bottle_count === 1 ? "" : "s"} · {data.wines.length} wine{data.wines.length === 1 ? "" : "s"} · {data.boxes.length} box{data.boxes.length === 1 ? "" : "es"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setAdding(true)} className="bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="location-add-wine-button"><Plus className="w-4 h-4 mr-2" />Add</Button>
          <Button onClick={() => setChecking(true)} disabled={checking} className="bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="confirm-inventory-button"><ClipboardCheck className="w-4 h-4 mr-2" />Confirm inventory</Button>
          <Button variant="outline" onClick={() => navigate("/scan")} data-testid="location-scan-button"><ScanLine className="w-4 h-4 mr-2" />Scan</Button>
          <Button variant="outline" onClick={() => print()} disabled={!data.bottle_count && !data.boxes.length} data-testid="location-print-button"><Printer className="w-4 h-4 mr-2" />Labels</Button>
        </div>
      </div>

      {checking && <InventoryPanel data={data} onCancel={() => setChecking(false)} onDone={(c) => { setChecking(false); setChecks((prev) => [c, ...prev]); }} />}
      <h3 className="font-serif text-2xl text-[#D4AF37] mb-4">Inventory</h3>
      <InventoryHistory checks={checks} />

      {!!data.boxes.length && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="location-boxes">
          {data.boxes.map((b) => (
            <span key={b.id} className="flex items-center gap-2 rounded-md border border-[#2E252A] bg-[#161214] px-3 py-2 text-sm" data-testid={`location-box-${b.barcode}`}>
              <Package className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-[#F5F0EB]">Box {b.box_number}</span>
              <span className="font-mono text-xs text-[#6E665D]">{b.barcode} · {b.bottle_count} btl</span>
            </span>
          ))}
        </div>
      )}

      <div className="space-y-4" data-testid="location-wines">
        <SelectionBar count={picked.length} onPrint={() => print(picked)} onClear={() => setPicked([])} />
        {data.wines.map((w) => (
          <div key={w.wine_id} className="rounded-xl border border-[#2E252A] bg-[#161214] p-5" data-testid={`location-wine-${w.wine_id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <Link to={`/wines/${w.wine_id}`} className="hover:text-[#D4AF37]">
                <p className="font-mono text-sm text-[#D4AF37]">{w.vintage || "NV"}</p>
                <p className="font-serif text-xl text-[#F5F0EB]">{w.name}</p>
                <p className="text-xs text-[#A89F91]">{w.producer}</p>
              </Link>
              <div className="text-right">
                <p className="font-serif text-2xl text-[#F5F0EB]">{w.bottles.length}</p>
                <DrinkBadge status={w.drink_status} />
              </div>
            </div>
            <BottleChips bottles={w.bottles} onEdit={(b) => setEditing(b)} selected={picked} onToggle={toggleIn(setPicked)} max={500} />
          </div>
        ))}
        {!data.wines.length && <p className="text-[#6E665D] py-10 text-center" data-testid="location-empty">No bottles at this location.</p>}
      </div>
      <WineFormDialog open={adding} onOpenChange={setAdding} onSaved={load} defaultLocation={id} />
      {editing && <BottleDetailsDialog bottles={[editing]} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
